import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAsync } from 'react-use';
import { BookApi } from '../../API/BookApi';
import { StorylineApi } from '../../API/StorylineApi';
import { UniverseApi } from '../../API/UniverseApi';
import { useAuth } from '../../Auth/AuthProvider';
import Button from '../../Components/Button';
import CoAuthorTag from '../../Components/CoAuthorTag';
import { FeatureIntroWizard } from '../../Components/FeatureIntroWizard';
import LoadingIcon from '../../Components/LoadingIcon';
import { AnalyticsEvent, useAnalytics } from '../../Context/AnalyticsProvider';
import { useNavigate } from '../../ProtectedRoute';
import {
  StorylineDilemma,
  StorylineDilemmaChoiceInput,
} from '../../Types/StorylineDilemma';
import { Universe } from '../../Types/Universe';
import { User } from '../../Types/User';
import { Feature } from '../../Utils/FeatureUsage';
import BuilderProgressBar from '../Controls/BuilderProgressBar';
import StorylineDilemmaOptionsSelectView from '../Controls/StorylineDilemmaOptionsSelectView';
import StorylineInputView from '../Controls/StorylineInputView';
import UniverseSelectionView from '../Controls/UniverseSelectionView';
import { BuilderGenerationStatus } from '../Types/BuilderGenerationStatus';
import BookBuilderInProgressView from './BookBuilderInProgressView';
import CoAuthorSelectionButton from './CoAuthorSelectionButton';
import { RewardsIntro } from './Intro/RewardsIntro';

type BookBuilderSlotType = 'universe' | 'storyline' | string;

interface LocationState {
  requestId?: string;
  generationStartedAt?: Date;
}

export const BookBuilderView: React.FC = () => {
  const navigate = useNavigate();
  const { trackEvent } = useAnalytics();
  const { isAuthenticated, signUpAsAnonymous } = useAuth();
  const { bookId } = useParams<{ bookId: string }>();
  const location = useLocation();
  const { requestId: initialRequestId, generationStartedAt } =
    (location.state || {}) as LocationState;

  const [generationStatus, setGenerationStatus] =
    useState<BuilderGenerationStatus>(
      bookId
        ? BuilderGenerationStatus.IN_PROGRESS
        : BuilderGenerationStatus.NOT_REQUESTED,
    );
  const [requestId, setRequestId] = useState<string>(initialRequestId);
  const [generationStartedAtState, setGenerationStartedAtState] =
    useState<Date | null>(generationStartedAt);
  const [universes, setUniverses] = useState<Universe[]>();
  const [dilemmas, setDilemmas] = useState<StorylineDilemma[]>();

  const [focusedSlot, setFocusedSlot] =
    useState<BookBuilderSlotType>('universe');

  const [selectedUniverseId, setSelectedUniverseId] = useState<string>();
  const [
    selectedDilemmaOptionByDilemmaId,
    setSelectedDilemmaOptionByDilemmaId,
  ] = useState<Record<string, string>>({});
  const [storyline, setStoryline] = useState<string>();
  const [searchString, setSearchString] = useState('');
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});

  const selectionAreaRef = React.useRef<HTMLDivElement>(null);

  const [coAuthor, setCoAuthor] = useState<User | null>(null);

  useEffect(() => {
    const fetchUniverses = async () => {
      try {
        const universeApi = new UniverseApi();
        const fetchedUniverses = await universeApi.getUniverses();
        setUniverses(fetchedUniverses);
      } catch (error) {
        console.error('Error fetching universes:', error);
      }
    };
    fetchUniverses();
  }, []);

  useAsync(async () => {
    const dilemmaApi = new StorylineApi();
    const result = await dilemmaApi.getGenericDilemmas();
    setDilemmas(result);
  }, []);

  const relevantDilemmas = useMemo(() => {
    return dilemmas?.filter(
      (dilemma) => dilemma.universeId === selectedUniverseId,
    );
  }, [dilemmas, selectedUniverseId]);

  const dilemmaChoices = useMemo<StorylineDilemmaChoiceInput[]>(() => {
    return dilemmas
      ?.filter((dilemma) => selectedDilemmaOptionByDilemmaId[dilemma.id])
      .map((dilemma) => ({
        dilemmaId: dilemma.id,
        chosenOptionId: selectedDilemmaOptionByDilemmaId[dilemma.id],
      }));
  }, [dilemmas, selectedDilemmaOptionByDilemmaId]);

  const SlotSequence = useMemo<BookBuilderSlotType[]>(() => {
    return [
      'universe',
      ...(relevantDilemmas
        ?.sort((a, b) => a.order - b.order)
        ?.map((dilemma) => dilemma.id) ?? []),
      // 'storyline', Skipping the storyline slot for now
    ];
  }, [relevantDilemmas]);

  const SlotsConfig: Record<string, any> = useMemo(() => {
    const result: any = {};

    result.universe = {
      heading: "Pick your Book's Universe",
      description:
        'Each world holds endless possibilities. Which one speaks to your imagination?',
      searchPlaceholder: null,
      isRequired: true,
      maxItems: 1,
    };

    result.storyline = {
      ...result.storyline,
      title: 'Add Your Own Magical Twist',
      description:
        'Your description will be used to guide the book which ever way you please.',
    };

    if (dilemmas) {
      for (const dilemma of dilemmas) {
        result[dilemma.id] = {
          heading: dilemma.question,
          description: dilemma.details,
          isRequired: true,
          maxItems: 1,
        };
      }
    }

    return result;
  }, [dilemmas]);

  function handleFocusedSlotCompleted(
    slot: BookBuilderSlotType,
    nextSlot?: BookBuilderSlotType,
  ) {
    const analyticsMetadata: any = {
      source: 'BookBuilderView',
      step: slot,
      nextStep: nextSlot,
    };
    if (slot === 'storyline') {
      analyticsMetadata.hasInput = !!storyline?.trim();
      analyticsMetadata.storyline = storyline?.trim() ?? '';
    } else if (slot === 'universe') {
      analyticsMetadata.selectedUniverse = selectedUniverseId;
      analyticsMetadata.universe = universes?.find(
        (u) => u.id === selectedUniverseId,
      );
    } else {
      // This is a dilemma slot
      const selectedOptionId = selectedDilemmaOptionByDilemmaId[slot];
      const dilemma = dilemmas?.find((d) => d.id === slot);

      // Find the selected option from the dilemma options
      const selectedOption = dilemma?.options?.find(
        (option) => option.id === selectedOptionId,
      );

      analyticsMetadata.selectedOption = selectedOptionId;
      analyticsMetadata.dilemmaId = dilemma?.id;
      analyticsMetadata.dilemmaQuestion = dilemma?.question;
      analyticsMetadata.selectedDilemmaOption = selectedOption;

      // Track the dilemma choice as submitted when completing this step
      trackEvent(AnalyticsEvent.DILEMMA_CHOICE_SUBMITTED, {
        source: 'BookBuilderView',
        dilemmaId: dilemma.id,
        dilemmaQuestion: dilemma.question,
        selectedOptionId: selectedOptionId,
        selectedDilemmaOption: selectedOption,
      });
    }
    trackEvent(AnalyticsEvent.BOOK_BUILDER_STEP_COMPLETED, analyticsMetadata);
  }

  function handleFocusedSlotChange(slot: BookBuilderSlotType) {
    if (slot === focusedSlot) return;

    handleFocusedSlotCompleted(focusedSlot, slot);

    setFocusedSlot((prev) => {
      setSearchString('');
      return slot;
    });

    selectionAreaRef?.current.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }

  const handleCoAuthorSelected = (coAuthor: User) => {
    setCoAuthor(coAuthor);
  };

  async function onSubmit() {
    setValidationErrors(undefined);
    handleFocusedSlotCompleted(focusedSlot);

    try {
      const startedAt = new Date();
      setGenerationStatus(BuilderGenerationStatus.REQUESTED);
      setGenerationStartedAtState(startedAt);

      await trackEvent(AnalyticsEvent.BOOK_BUILDER_GENERATION_REQUESTED, {
        source: 'BookBuilderView',
        selectedUniverse: selectedUniverseId,
        universe: universes?.find((u) => u.id === selectedUniverseId),
        selectedDilemmaOptions: selectedDilemmaOptionByDilemmaId,
        dilemmas: dilemmas?.filter(
          (dilemma) => selectedDilemmaOptionByDilemmaId[dilemma.id],
        ),
        storyline: storyline,
        coAuthorId: coAuthor?.id,
      });

      if (!isAuthenticated) {
        await signUpAsAnonymous();
      }

      const bookApi = new BookApi();
      const response = await bookApi.triggerBookGeneration({
        universeId: selectedUniverseId,
        storyline,
        dilemmaChoices,
        coAuthorUserId: coAuthor?.id,
      });

      await trackEvent(
        AnalyticsEvent.BOOK_BUILDER_GENERATION_REQUEST_COMPLETED,
        {
          source: 'BookBuilderView',
          selectedUniverse: selectedUniverseId,
          universe: universes?.find((u) => u.id === selectedUniverseId),
          selectedDilemmaOptions: selectedDilemmaOptionByDilemmaId,
          dilemmas: dilemmas?.filter(
            (dilemma) => selectedDilemmaOptionByDilemmaId[dilemma.id],
          ),
          storyline: storyline,
          bookId: response.newBookId,
          requestId: response.requestId,
          coAuthorId: coAuthor?.id,
        },
      );

      setValidationErrors({});
      setGenerationStatus(BuilderGenerationStatus.IN_PROGRESS);

      navigate(`/create-my-own-story/${response.newBookId}`, {
        state: {
          requestId: response.requestId,
          generationStartedAt: startedAt,
        },
        replace: true,
      });
    } catch (ex) {
      if (
        ex.response &&
        ex.response.status === 422 &&
        ex.response.data.errors
      ) {
        setValidationErrors(ex.response.data.errors);

        trackEvent(AnalyticsEvent.BOOK_BUILDER_GENERATION_REQUEST_FAILED, {
          source: 'BookBuilderView',
          error: 'validation',
          storyline: storyline,
          validationErrors: ex.response.data.errors,
        });
      } else {
        toast.error(
          'An error occurred while creating your story. Please try again.',
        );

        trackEvent(AnalyticsEvent.BOOK_BUILDER_GENERATION_REQUEST_FAILED, {
          source: 'BookBuilderView',
          error: 'unknown',
          message: ex.message,
        });
      }
      console.error(ex);
      setGenerationStatus(BuilderGenerationStatus.NOT_REQUESTED);
    }
  }

  function canProceedToNextStep() {
    switch (focusedSlot) {
      case 'universe':
        return !SlotsConfig[focusedSlot].isRequired || selectedUniverseId;
      case 'storyline':
        return true; // No storyline is allowed
      default:
        return (
          !SlotsConfig[focusedSlot].isRequired ||
          selectedDilemmaOptionByDilemmaId[focusedSlot]
        );
    }
  }

  const handleUniverseCreated = useCallback(
    (universe: Universe, dilemmas: StorylineDilemma[]) => {
      setUniverses((prev) => [...prev, universe]);
      setSelectedUniverseId(universe.id);
      setDilemmas((prev) => [...prev, ...dilemmas]);
    },
    [],
  );

  if (generationStatus === BuilderGenerationStatus.IN_PROGRESS) {
    return (
      <BookBuilderInProgressView
        requestId={requestId}
        bookId={bookId}
        onGenerationError={() => {
          setGenerationStatus(BuilderGenerationStatus.NOT_REQUESTED);
        }}
      />
    );
  }

  const isInitialized = !!universes && !!dilemmas;
  if (!isInitialized) {
    return <LoadingIcon fullScreen />;
  }

  return (
    <div className="relative w-full h-full flex-1 overflow-hidden">
      <FeatureIntroWizard
        feature={Feature.BOOK_BUILDER}
        maxWidthClass="max-w-[600px] max-md:max-w-[400px] max-sm:max-w-[350px]"
      >
        <RewardsIntro key="rewards" />
      </FeatureIntroWizard>
      <div
        className="w-full h-full flex flex-row justify-center short:py-1 py-12 
        max-lg:py-0 
        max-vxl:py-0"
      >
        <div
          className="
          flex flex-col space-y-0 short:h-full
          vxl:lg:w-3/4 vxl:lg:h-[80dvh] vxl:lg:max-w-[1152px]
          max-lg:flex-1 max-lg:h-full max-lg:space-y-0
          max-vxl:flex-1 max-vxl:h-full max-vxl:space-y-0
        "
        >
          <div
            className="flex gap-5 short:p-2 p-4 bg-white rounded-t-2xl border-b border-solid border-gray-200 justify-start items-center
          max-lg:rounded-none max-md:flex-wrap
          max-vxl:rounded-none max-vxl:flex-wrap"
          >
            <h2
              className="text-3xl font-semibold leading-9 text-neutral-800
            max-lg:text-xl max-lg:font-semibold
            max-vxl:text-xl max-vxl:font-semibold
            short:hidden"
            >
              Create a Book
            </h2>
            {coAuthor ? (
              <CoAuthorTag
                coAuthor={coAuthor}
                allowRemove={true}
                onRemove={() => setCoAuthor(null)}
              />
            ) : (
              <CoAuthorSelectionButton
                selectedCoAuthor={coAuthor}
                onSelectCoAuthor={handleCoAuthorSelected}
              />
            )}
            <BuilderProgressBar
              focusedSlot={focusedSlot}
              slotSequence={SlotSequence}
              onSlotChange={handleFocusedSlotChange}
            />
          </div>
          <div
            ref={selectionAreaRef}
            className="
                  flex-1 flex flex-col gap-5 px-4 overflow-hidden bg-white border-b border-solid border-gray-200 border-opacity-30
                  max-lg:gap-0
                  max-vxl:gap-0
                "
            data-testid={`focused-slot-${focusedSlot}`}
          >
            {focusedSlot === 'storyline' ? (
              <StorylineInputView
                storyline={storyline}
                title={SlotsConfig[focusedSlot].title}
                description={SlotsConfig[focusedSlot].description}
                validationError={validationErrors?.['storyline']}
                onChange={(value) => {
                  setStoryline(value);
                  if (validationErrors?.['storyline']) {
                    setValidationErrors((prevState) => {
                      delete prevState['storyline'];
                      return { ...prevState };
                    });
                  }
                }}
                onSubmit={onSubmit}
                onRandomSelection={() => {}}
              />
            ) : focusedSlot === 'universe' ? (
              <UniverseSelectionView
                key={focusedSlot}
                universes={universes}
                selectedUniverseId={selectedUniverseId}
                searchString={searchString}
                onSearch={setSearchString}
                onSelectionChange={(universeId: string) => {
                  if (universeId !== selectedUniverseId) {
                    setSelectedUniverseId(universeId);
                    setSelectedDilemmaOptionByDilemmaId({});
                  }
                }}
                onUniverseCreated={handleUniverseCreated}
                onUniverseEdited={(universe) => {
                  setUniverses((prev) =>
                    prev.map((u) => (u.id === universe.id ? universe : u)),
                  );
                }}
              />
            ) : (
              <StorylineDilemmaOptionsSelectView
                key={focusedSlot}
                dilemma={dilemmas.find((dilemma) => dilemma.id === focusedSlot)}
                selectedOptionIds={[
                  selectedDilemmaOptionByDilemmaId[focusedSlot],
                ]}
                onOptionSelect={(optionId: string) => {
                  setSelectedDilemmaOptionByDilemmaId((prev) => ({
                    ...prev,
                    [focusedSlot]: optionId,
                  }));
                }}
                onOptionDeleted={(optionId: string) => {
                  setDilemmas((prev) =>
                    prev?.map((dilemma) => ({
                      ...dilemma,
                      options: dilemma.options.filter((o) => o.id !== optionId),
                    })),
                  );
                }}
                searchString={searchString}
                onSearch={setSearchString}
              />
            )}
          </div>

          <div
            className="flex flex-col gap-5 short:py-1 short:px-4 p-4 bg-white rounded-b-2xl
              max-lg:rounded-none max-lg:p-3 
              max-md:flex-wrap 
              max-vxl:rounded-none max-vxl:p-3 max-vxl:flex-wrap"
          >
            <div
              className="
                flex justify-between items-start
              "
            >
              <Button
                className={`${SlotSequence.indexOf(focusedSlot) === 0 && 'invisible'}`}
                disabled={
                  generationStatus === BuilderGenerationStatus.REQUESTED
                }
                onClick={() =>
                  handleFocusedSlotChange(
                    SlotSequence[SlotSequence.indexOf(focusedSlot) - 1],
                  )
                }
              >
                Back
              </Button>

              {SlotSequence.indexOf(focusedSlot) < SlotSequence.length - 1 ? (
                <Button
                  testId="next-step-button"
                  disabled={
                    !canProceedToNextStep() ||
                    generationStatus === BuilderGenerationStatus.REQUESTED
                  }
                  onClick={() =>
                    handleFocusedSlotChange(
                      SlotSequence[SlotSequence.indexOf(focusedSlot) + 1],
                    )
                  }
                >
                  Next
                </Button>
              ) : (
                <Button
                  disabled={!canProceedToNextStep()}
                  isInProgress={
                    generationStatus === BuilderGenerationStatus.REQUESTED
                  }
                  inProgressText="Creating Your Book..."
                  testId="submit-button"
                  onClick={onSubmit}
                >
                  Create Book
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookBuilderView;
